import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { CatalogueSearchFilterType } from "@/shared/enums/catalogue-search-filter-type";
import DataGrid, {
	DataGridTypes,
	FilterRow,
	Selection,
} from "devextreme-react/data-grid";
import DropDownBox from "devextreme-react/drop-down-box";
import { useEffect, useRef } from "react";

interface ICatalogueDataGridProps {
	items: any[];
	type: CatalogueSearchFilterType;
	keyExpr?: string;
	displayExpr?: string;
	selectionMode?: "multiple" | "single";
	placeholder?: string;
	currentValue: any[];
	label?: string;
	columnCaption?: string;
	onSelectionChange: (
		selectedValues: any[],
		type: CatalogueSearchFilterType,
	) => void;
	getDropDownHint: (type: CatalogueSearchFilterType) => any;
}

export default function CatalogueDataGrid({
	items,
	type,
	keyExpr = "name",
	displayExpr = "name",
	placeholder,
	currentValue,
	selectionMode = "multiple",
	onSelectionChange,
	getDropDownHint,
	label = "",
	columnCaption = "",
}: Readonly<ICatalogueDataGridProps>) {
	const { t } = useLanguage();
	const dropDownBoxRef = useRef<any>(null);
	const isSelectionSyncingRef = useRef(false);
	let gridRef: any = null;

	const setGridRef = (ref: any) => {
		gridRef = ref;
	};

	const dataGridOnSelectionChanged = (
		e: DataGridTypes.SelectionChangedEvent,
		type: CatalogueSearchFilterType,
		keyExpr: string,
	) => {
		if (isSelectionSyncingRef.current) {
			return;
		}

		const selectedValues: any = e.selectedRowKeys.map((x) => x[keyExpr]);

		if (JSON.stringify(selectedValues) !== JSON.stringify(currentValue)) {
			onSelectionChange(selectedValues, type);
		}
	};

	const dataGridContentReady = (e: any, items: any[]) => {
		if (!isSelectionSyncingRef.current) {
			isSelectionSyncingRef.current = true;
			try {
				if (currentValue && currentValue.length > 0) {
					e.component.clearSelection();

					const keysToSelect = items.filter((item: any) =>
						currentValue.includes(item[keyExpr]),
					);

					if (keysToSelect.length > 0) {
						e.component.selectRows(keysToSelect, false);
					}
				} else {
					e.component.clearSelection();
				}
			} finally {
				setTimeout(() => {
					isSelectionSyncingRef.current = false;
				}, 100);
			}
		}
	};

	function setGridColumns(displayExpr: string, columnCaption: string) {
		return [
			{
				dataField: displayExpr,
				caption: columnCaption || t(`catalogue.${displayExpr}`),
			},
		];
	}

	useEffect(() => {
		if (currentValue && currentValue.length > 0 && gridRef) {
			const keysToSelect = items.filter((item: any) =>
				currentValue.includes(item[keyExpr]),
			);
			if (keysToSelect.length > 0) {
				gridRef.instance().selectRows(keysToSelect, false);
			}
		}
	}, [currentValue, items, keyExpr]);

	const syncDataGridSelection = (e: any) => {
		if (e.value !== undefined) {
			if (e.value === null) {
				if (gridRef?.instance()) {
					isSelectionSyncingRef.current = true;
					gridRef.instance().selectRows([]);
					setTimeout(() => {
						isSelectionSyncingRef.current = false;
					}, 200);
				}
				onSelectionChange([], type);
			}
		}
	};

	const dataGridRender = () => {
		return (
			<DataGrid
				ref={(ref) => {
					setGridRef(ref);
				}}
				dataSource={items}
				columns={setGridColumns(displayExpr, columnCaption)}
				hoverStateEnabled={true}
				showRowLines={false}
				height={300}
				scrolling={{ mode: "standard" }}
				onSelectionChanged={(e: any) =>
					dataGridOnSelectionChanged(e, type, keyExpr)
				}
				onContentReady={(e) => dataGridContentReady(e, items)}
				elementAttr={{ class: "basic-grid catalogue-grid-dropdown" }}
			>
				<Selection
					mode={selectionMode}
					selectAllMode="allPages"
					showCheckBoxesMode="always"
					allowSelectAll={true}
				/>
				<FilterRow visible={true} />
			</DataGrid>
		);
	};

	if (!label) {
		if (type === CatalogueSearchFilterType.categories) {
			label = t("catalogue.category-label");
		} else if (type === CatalogueSearchFilterType.owners) {
			label = t("catalogue.owner-label");
		} else if (type === CatalogueSearchFilterType.types) {
			label = t("catalogue.type-label");
		}
	}

	return (
		<>
			<div className="dark-label">{label}</div>
			<DropDownBox
				ref={dropDownBoxRef}
				hint={getDropDownHint(type)}
				elementAttr={{ class: "select-sm catalogue-dropdown" }}
				className="catalogue-search-option-select"
				value={currentValue}
				tabIndex={0}
				valueExpr={keyExpr}
				displayExpr={displayExpr}
				placeholder={placeholder || t("catalogue.select")}
				showClearButton={true}
				dataSource={items}
				deferRendering={true}
				onOpened={() => {
					isSelectionSyncingRef.current = false;
				}}
				onValueChanged={(e: any) => {
					if (isSelectionSyncingRef.current) {
						return;
					}
					syncDataGridSelection(e);
				}}
				contentRender={dataGridRender}
			/>
		</>
	);
}
